import { api, throwApiError } from "../../../api/api";
import type { PaymentAttachments, PaymentAttachmentsPayload } from "./paymentFilesApi";
import type { DpInssPaymentStatus } from "./dpTypes";
import type { DpGuideFinance } from "./financeBridge";

/** Guia e comprovante: só hasGuide/hasReceipt e o nome; o arquivo sai por /dp/<recolhimento>/:id/file. */
export type DpInssPayment = PaymentAttachments & {
  id: string;
  employerId: string;
  year: number;
  /** 1–12; 13 = competência do 13º salário. */
  month: number;
  /** Valores em reais como string ("41250.33"), já com duas casas. */
  amount: string;
  penaltyAmount: string;
  totalAmount: string;
  paidAt: string | null;
  /** Derivado pelo backend: dia 20 do mês seguinte (13º: 20/12 do próprio ano). */
  dueDate: string;
  status: DpInssPaymentStatus;
  documentNumber?: string | null;
  notes?: string | null;
  /** Título de previsão no Financeiro (ADR 0029); null = sem título. */
  financeiro: DpGuideFinance;
  /** Ex.: "Pago em DD/MM/AAAA no DP, sem baixa no Financeiro." */
  financeiroWarning: string | null;
};

export type DpInssPaymentList = {
  items: DpInssPayment[];
  totals: { amount: string; penalty: string; total: string };
};

export type CreateInssPaymentPayload = PaymentAttachmentsPayload & {
  employerId: string;
  year: number;
  month: number;
  amount: number;
  penaltyAmount?: number;
  paidAt?: string | null;
  documentNumber?: string | null;
  notes?: string | null;
};

/** A competência (empregador, ano e mês) não muda: lançamento errado se exclui e relança. */
export type UpdateInssPaymentPayload = Partial<
  Omit<CreateInssPaymentPayload, "employerId" | "year" | "month">
>;

const auth = (token: string) => {
  const raw = token?.startsWith("Bearer ") ? token.slice(7) : token;
  return { headers: { Authorization: `Bearer ${raw}` } };
};

export const listInssPaymentsApi = async (
  token: string,
  params: { employerId?: string; year?: number }
): Promise<DpInssPaymentList> => {
  try {
    const { data } = await api.get<DpInssPaymentList>("/dp/inss-payments", {
      ...auth(token),
      params,
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar recolhimentos.");
    throw error;
  }
};

export const createInssPaymentApi = async (
  token: string,
  payload: CreateInssPaymentPayload
): Promise<DpInssPayment> => {
  try {
    const { data } = await api.post<DpInssPayment>("/dp/inss-payments", payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao lançar competência.");
    throw error;
  }
};

export const updateInssPaymentApi = async (
  token: string,
  id: string,
  payload: UpdateInssPaymentPayload
): Promise<DpInssPayment> => {
  try {
    const { data } = await api.patch<DpInssPayment>(`/dp/inss-payments/${id}`, payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao editar recolhimento.");
    throw error;
  }
};

export const deleteInssPaymentApi = async (token: string, id: string): Promise<void> => {
  try {
    await api.delete(`/dp/inss-payments/${id}`, auth(token));
  } catch (error) {
    throwApiError(error, "Erro ao excluir recolhimento.");
    throw error;
  }
};
